import { useSyncExternalStore } from "react";

// Minimal external store for the few UI-facing diagnostics values the
// Contact-support page reads. The collector (vanilla, non-React) writes here;
// EKYCManager subscribes via useDiagnosticsState(). This is React 18's built-in
// external-store primitive — used instead of adding a zustand dependency, since
// web/ on main does not ship zustand and adding a web library needs sign-off.

export const DOC_SCAN_FAIL_THRESHOLD = 3;
export const SERVER_TIMEOUT_THRESHOLD = 2;

let state = {
  docScanFailCount: 0,
  serverTimeoutCount: 0,
  supportRequested: false,
  referenceCode: null,
};

const listeners = new Set();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

function setState(patch) {
  const next = { ...state, ...patch };
  // Skip no-op updates so useSyncExternalStore doesn't churn
  let changed = false;
  for (const k of Object.keys(patch)) {
    if (state[k] !== next[k]) {
      changed = true;
      break;
    }
  }
  if (!changed) return;
  state = next;
  emit();
}

function incr(key, by = 1) {
  setState({ [key]: (state[key] || 0) + by });
}

// Raise a counter to at least `value` (server-authoritative counters like
// validationCount are monotonic and should never be lowered by a stale batch).
function raiseTo(key, value) {
  if (typeof value !== "number" || Number.isNaN(value)) return;
  if ((state[key] || 0) < value) setState({ [key]: value });
}

function reset() {
  setState({ docScanFailCount: 0, serverTimeoutCount: 0 });
}

export function shouldShowContactSupport(s = state) {
  return (
    (s.docScanFailCount || 0) >= DOC_SCAN_FAIL_THRESHOLD ||
    (s.serverTimeoutCount || 0) >= SERVER_TIMEOUT_THRESHOLD
  );
}

export function useDiagnosticsState() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

export const diagnosticsStore = {
  subscribe,
  getSnapshot,
  setState,
  incr,
  raiseTo,
  reset,
};
